import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CSSObject } from '@emotion/react';
import useTheme from '@hooks/useTheme';
import useBreakpoints from '@hooks/useBreakpoints';
import { convertToHtml } from '@cvent/rich-text-renderer';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { useTranslate } from 'nucleus-text';
import TextLink from '@cvent/carina/components/TextLink';
import useSafariLineClamp from '@hooks/useSafariLineClamp';

// Rich text content is represented as an object with the following properties
type RichTextContent = {
  format: string;
  version: number;
  content: Record<string, unknown> | string;
};

type RichTextEventDescriptionProps = {
  /** Event description */
  eventDescription: string;
  /** To enable expandable event description */
  enableExpandableDescription?: boolean;
  /** Number of lines after the description text is cut */
  numberOfLines?: number;
  customContainerStyle?: CSSObject;
};

// The event description must be formatted to either a string or an object(RichTextContent) before passing to the RichTextRenderer
const formatRichTextData = (eventDescriptiondata: string): RichTextContent | string => {
  try {
    return JSON.parse(eventDescriptiondata);
  } catch (e) {
    return eventDescriptiondata?.toString();
  }
};

const useStyles = (): Record<string, CSSObject> => {
  const { font } = useTheme();
  const color = font.color.base;
  const { mediaIsMobile, mediaIsS } = useBreakpoints();

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    text: {
      width: '100%',
      fontSize: font.base.size[6],
      [mediaIsMobile]: {
        fontSize: font.base.size[4]
      },
      [mediaIsS]: {
        fontSize: font.base.size[4]
      },
      // Text is wrapped in a <span> when the event description text is styled with bold, italic, or underline
      span: {
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        color: `${color} !important`
      },
      // Supported header sizes: h3 (Heading 1), h4 (Heading 2)
      'h3, h4, ul, ol, p, a': {
        color: `${color} !important`
      },
      // To override the fontSize from Blocks style and to provide visual consistency within the rich text content
      // Note: Blocks style was returning a higher fontSize value for 'p' than 'h4'
      h3: {
        marginBlockStart: 8,
        marginBlockEnd: 8,
        fontSize: font.base.size[7],
        fontWeight: 600,
        [mediaIsMobile]: {
          fontSize: font.base.size[6]
        },
        [mediaIsS]: {
          fontSize: font.base.size[6]
        }
      },
      h4: {
        marginBlockStart: 8,
        marginBlockEnd: 8,
        // Require 21px font size and the only token available for that font size is font.base.size.h3
        fontSize: font.base.size.h3,
        fontWeight: 600,
        [mediaIsMobile]: {
          fontSize: font.base.size[5]
        },
        [mediaIsS]: {
          fontSize: font.base.size[5]
        }
      },
      p: {
        marginBlockStart: 8,
        marginBlockEnd: 16,
        fontSize: font.base.size[6],
        [mediaIsMobile]: {
          fontSize: font.base.size[4]
        },
        [mediaIsS]: {
          fontSize: font.base.size[4]
        }
      },
      'ul, ol': {
        marginLeft: 12, // To fix rich-text-renderer not adding enough margin at the left of ordered-list-item/unordered-list-item
        marginBlockEnd: 16,
        fontSize: font.base.size[6],
        [mediaIsMobile]: {
          fontSize: font.base.size[4]
        },
        [mediaIsS]: {
          fontSize: font.base.size[4]
        }
      }
    },
    moreOrLessButtonStyle: {
      marginTop: 8,
      textDecoration: 'underline',
      fontWeight: font.base.weight.bold,
      fontSize: font.base.size[4],
      [mediaIsMobile]: {
        fontSize: font.base.size[3]
      },
      [mediaIsS]: {
        fontSize: font.base.size[3]
      }
    }
  };
};

function RichTextEventDescription({
  eventDescription = '',
  enableExpandableDescription = false,
  numberOfLines = 5,
  customContainerStyle = {}
}: RichTextEventDescriptionProps): JSX.Element {
  const { translate } = useTranslate();
  // To enable line clamp to limit the number of lines of text that displayed
  const [enableLineClamp, setEnableLineClamp] = useState(enableExpandableDescription);
  const [textExpanded, setTextExpanded] = useState(false);
  // Following states are required to determine whether to show Read more / Read less TextLink
  const [clientHeight, setClientHeight] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);

  const richTextData = formatRichTextData(eventDescription);
  const styles = useStyles();

  const { ref: textRef, clamp } = useSafariLineClamp(numberOfLines);
  const isShowMoreClicked = useRef(false);

  const calculateTextHeight = useCallback(() => {
    if (enableExpandableDescription && eventDescription && textRef.current) {
      setClientHeight(textRef.current.clientHeight);
      setScrollHeight(textRef.current.scrollHeight);
    }
  }, [eventDescription, enableExpandableDescription, textRef]);

  useEffect(() => {
    if (enableExpandableDescription) {
      // Event to detect the resizing of the screen in order to recalculate clientHeight and scrollHeight
      window.addEventListener('resize', calculateTextHeight);
      calculateTextHeight();
    }

    return () => {
      if (enableExpandableDescription) {
        window.removeEventListener('resize', calculateTextHeight);
      }
    };
  }, [calculateTextHeight, enableExpandableDescription]);

  return (
    <div css={[styles.container, customContainerStyle]}>
      <div
        ref={textRef}
        css={[styles.text, ...(enableLineClamp ? [clamp] : [])]}
        dangerouslySetInnerHTML={{
          __html: convertToHtml(richTextData, { withStyles: false }).toString()
        }}
        {...injectTestId('rich-text-event-description')}
      />
      <div>
        {enableExpandableDescription && (scrollHeight > clientHeight || isShowMoreClicked.current) && (
          <TextLink
            element="button"
            css={styles.moreOrLessButtonStyle}
            onClick={() => {
              if (textExpanded) {
                setEnableLineClamp(true);
              } else {
                setEnableLineClamp(false);
                isShowMoreClicked.current = true;
              }
              setTextExpanded(!textExpanded);
            }}
            testID="rich-text-event-description-readmore-readless-btn"
          >
            <span>
              {textExpanded
                ? translate('LandingPage-ExpandableText-LessText')
                : translate('LandingPage-ExpandableText-MoreText')}
            </span>
          </TextLink>
        )}
      </div>
    </div>
  );
}

export default RichTextEventDescription;
